import { ScatterplotLayer, ScatterplotLayerProps } from "@deck.gl/layers"
import { CompositeLayer, PickingInfo } from "@deck.gl/core"
import { get } from "nodash"
import store from "integrations/redux/store"
import { customMapController, disabledMapController } from "features/map/control/slice"
import { Midpoint } from "./GeometryLayer.class"
import { LngLatCoordinatePair } from "features/map/event/types"
import { geometryDataStore } from "./GeometryDataStore"
import { getGlobalTerrainData } from "features/map/GlobalTerrainManager"
import { resolveDeckPickingLngLat } from "plugins/resolveLngLat"
import { Geometry } from "./types"

export default class DrawPerimeterMidpointsLayer<
    T extends Geometry = Geometry,
> extends CompositeLayer<{
    addPoint: (coordinate: LngLatCoordinatePair, index?: number) => void
    midpointStyles: Partial<ScatterplotLayerProps<T>>
    entityId?: string
}> {
    state = {} as {
        data: LngLatCoordinatePair[]
        original: LngLatCoordinatePair
        isUpdating: boolean
    }
    static layerName = "geometry_midpoints"
    dispatch = store.dispatch
    updateState({ changeFlags }: { changeFlags: Record<string, unknown> }) {
        const data = get(this.props, "data", {})
        if (changeFlags.dataChanged && !this.state.isUpdating) {
            this.setState({ data })
        }
    }

    onClick(info: PickingInfo) {
        const { index } = info
        const resolved = resolveDeckPickingLngLat(info)
        if (resolved) {
            const lngLat = [resolved.longitude, resolved.latitude] as LngLatCoordinatePair
            this.props?.addPoint(lngLat, index + 1)
        }

        return false
    }

    onDragStart(info: PickingInfo) {
        const { index } = info
        this.dispatch(disabledMapController())
        const resolved = resolveDeckPickingLngLat(info)
        const lngLat = [resolved?.longitude, resolved?.latitude] as LngLatCoordinatePair

        this.props.addPoint(lngLat, index + 1)
        this.setState({ original: lngLat })
    }

    onDrag(info: PickingInfo) {
        const { index } = info
        const resolved = resolveDeckPickingLngLat(info)
        if (resolved) {
            const lngLat = [resolved.longitude, resolved.latitude] as LngLatCoordinatePair
            geometryDataStore.updateShallow(lngLat, index + 1)
        }
    }

    onDragEnd(info: PickingInfo) {
        const { index } = info
        const resolved = resolveDeckPickingLngLat(info)
        if (resolved) {
            const lngLat = [resolved.longitude, resolved.latitude] as LngLatCoordinatePair
            geometryDataStore.updatePoint(lngLat, index + 1, this.state.original)
        }
        this.dispatch(
            customMapController({ options: { dragRotate: false, doubleClickZoom: false } })
        )
    }

    renderLayers() {
        return [
            new ScatterplotLayer(
                this.getSubLayerProps({
                    id: this.props.id,
                    data: this.state.data,
                    stroked: true,
                    filled: true,
                    getFillColor: [0, 0, 0, 0],
                    getLineColor: [37, 115, 233, 255],
                    lineWidthMinPixels: 1,
                    lineWidthMaxPixels: 1,
                    radiusMinPixels: 7,
                    radiusMaxPixels: 7,
                    extensions: [...getGlobalTerrainData().extensions],
                    getPosition: (d: Midpoint) => d.midpoint,
                    pickable: true,
                    ...this.props.midpointStyles,
                    parameters: { depthTest: false },
                })
            ),
        ]
    }
}
